/**
 * Tutor card for the management {@link DataTable}.
 * Compact layout: name, situation, optional contact / establishment / milestones.
 */
import * as React from 'react';
import { User, Building2 } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import type { TutorInPersonTabView } from '@/components/tutors/tutor-configs';
import { useDepartments } from '@/hooks/useDepartments';
import type { TutorRecord } from '@/types/inperson/tutor.types';
import {
  deriveTutorRowDisplay,
  deriveTutorRowActionFlags,
  showsTutorCardContact,
  showsTutorCardEstablishment,
  type TutorPageActions,
} from './tutor-table-utils';

/** Props for {@link TutorCard}. */
interface TutorCardProps {
  record: TutorRecord;
  tabView?: TutorInPersonTabView;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onManageAssignment?: (record: TutorRecord) => void;
  pageActions?: TutorPageActions;
}

/** Tutor card for the management {@link DataTable}. */
export const TutorCard: React.FC<TutorCardProps> = ({
  record,
  tabView = 'tutors',
  onView,
  onEdit,
  onDelete,
  onManageAssignment,
  pageActions,
}) => {
  const { data: departments = [] } = useDepartments('FR');
  /** Flattened labels/flags for this card. */
  const display = deriveTutorRowDisplay(record, departments);
  /** Edit / delete / assignment CTA flags from RBAC + current tab. */
  const actions = deriveTutorRowActionFlags(record, tabView, pageActions);

  return (
    <TableCard onClick={() => onView?.(record.userId)}>
      <div className="data-card-content">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]">
            <User className="w-5 h-5 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-semibold text-foreground text-base truncate">
              {display.displayName}
            </span>
            <LabelBadge
              label={display.situationConfig.label}
              icon={display.SituationIcon}
              variant={display.situationConfig.badgeVariant}
              color={display.situationConfig.badgeColor}
              size="sm"
            />
          </div>
        </div>

        {showsTutorCardContact(tabView) && (
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <display.contact.primary.icon
                className="w-4 h-4 shrink-0"
                style={{ color: 'var(--color-1-nuance-1)' }}
              />
              <span className="text-sm text-foreground truncate">
                {display.contact.primary.value}
              </span>
            </div>
            {display.contact.secondaryValue && (
              <span className="text-xs text-muted-foreground pl-6">
                {display.contact.secondaryValue}
              </span>
            )}
          </div>
        )}

        {showsTutorCardEstablishment(tabView) && display.firstWishName && (
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className="text-sm text-foreground truncate">{display.firstWishName}</span>
          </div>
        )}

        {display.hasDepartments && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Dép.</span>
            <span className="text-sm text-foreground">{display.departments.join(', ')}</span>
          </div>
        )}

        {tabView === 'pre_registered' && (
          <div className="flex items-center gap-1.5">
            {display.milestones.map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.key} className="flex flex-col items-center gap-0.5" title={m.label}>
                  <Icon className="w-4 h-4" style={{ color: m.color }} />
                  <span className="text-[10px]" style={{ color: m.color }}>
                    {m.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2 flex-wrap">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(record.userId);
            }}
          />
          {actions.canEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(record.userId);
              }}
            />
          )}
          {actions.canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(record.userId);
              }}
            />
          )}
          {actions.assignmentButtonPreset && (
            <Button
              preset={actions.assignmentButtonPreset}
              disabled={actions.isAssignmentButtonDisabled}
              onClick={(e) => {
                e.stopPropagation();
                onManageAssignment?.(record);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
